import { useCallback, useMemo } from "react"

import { parse } from "date-fns"
import { Formik, Form } from "formik"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { CloseIcon, SearchIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import {
    Chip,
    FormikDatePicker,
    IconButton,
    InputAdornment,
    ListItemText,
    ScrollableLayout,
    useIsMobileDevice,
} from "@l2r-front/l2r-ui"

import { documentsFiltersConfig } from "../../constants/documentsFiltersConfig"
import {
    ATTACHMENT_TYPE_FILTER,
    AUTHOR_FILTER,
    DATE_FROM_FILTER,
    DATE_TO_FILTER,
    FILE_NAME_FILTER,
    FILE_TYPE_FILTER,
    FILTERS,
    TAGS_FILTER,
} from "../../constants/filters"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { ModulesInfo } from "../../constants/modules"
import { useGetAttachmentTypes } from "../../hooks/useGetAttachmentTypes"

import * as Styled from "./DocumentFiltersForm.styled"

const defaultFormValues = {
    [ATTACHMENT_TYPE_FILTER]: [],
    [AUTHOR_FILTER]: [],
    [DATE_FROM_FILTER]: null,
    [DATE_TO_FILTER]: null,
    [FILE_NAME_FILTER]: "",
    [FILE_TYPE_FILTER]: [],
    [TAGS_FILTER]: [],
}

export function DocumentFiltersForm(props) {

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()
    const attachmentTypes = useGetAttachmentTypes()

    const {
        className,
        close,
        enabledFilters,
        fileTypes,
        filters,
        onSubmit,
        project,
        setFilters,
        users,
    } = props

    const initialValues = useMemo(() => {
        return ({
            ...defaultFormValues,
            ...filters,
            [AUTHOR_FILTER]: filters[AUTHOR_FILTER] ? users?.filter(user => filters[AUTHOR_FILTER].includes(user.uuid)) : [],
            [DATE_FROM_FILTER]: filters[DATE_FROM_FILTER] ? parse(filters[DATE_FROM_FILTER], "dd-MM-yyyy", new Date()) : null,
            [DATE_TO_FILTER]: filters[DATE_TO_FILTER] ? parse(filters[DATE_TO_FILTER], "dd-MM-yyyy", new Date()) : null,
            [FILE_TYPE_FILTER]: filters[FILE_TYPE_FILTER] ? fileTypes?.filter(fileType => filters[FILE_TYPE_FILTER].includes(fileType.name)) : [],
        })
    }, [filters, fileTypes, users])

    const getFormFields = useCallback((setFieldValue, values) => {
        return <Styled.InputsWrapper>
            {
                enabledFilters.includes(FILE_NAME_FILTER) && <Styled.TextField
                    id="fileName-filters-select"
                    name={FILE_NAME_FILTER}
                    placeholder={t(I18N_NAMESPACE, documentsFiltersConfig[FILE_NAME_FILTER].label)}
                    type="text"
                    value={values[FILE_NAME_FILTER]}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">
                            <SearchIcon color="objects/object-black" />
                        </InputAdornment>,
                        endAdornment: values[FILE_NAME_FILTER]?.length ? (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="clear input"
                                    onClick={() => setFieldValue(FILE_NAME_FILTER, "")}
                                    edge="end"
                                    size="small"
                                >
                                    <CloseIcon color="objects/object-black" fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            }
            {
                enabledFilters.includes(FILE_TYPE_FILTER) && <Styled.AutoCompleteFilter
                    defaultValue={initialValues[FILE_TYPE_FILTER]}
                    disableClearable
                    getOptionLabel={option => option.name}
                    id="fileType-filters-select"
                    isOptionEqualToValue={(option, value) => option.name === value.name}
                    label={t(I18N_NAMESPACE, documentsFiltersConfig[FILE_TYPE_FILTER].label)}
                    limitTags={2}
                    name={FILE_TYPE_FILTER}
                    options={fileTypes || []}
                    renderOption={(props, option) => {
                        return (
                            <li {...props}>
                                <Styled.ListItemIcon>
                                    <Styled.Image src={option.icon} />
                                </Styled.ListItemIcon>
                                <ListItemText primary={option.name} />
                            </li>
                        )
                    }}
                    setFieldValue={setFieldValue} />
            }
            {
                enabledFilters.includes(ATTACHMENT_TYPE_FILTER) && <Styled.AutoCompleteFilter
                    disableClearable
                    getOptionLabel={option => t(I18N_NAMESPACE, ModulesInfo[option].labelKey)}
                    id="attachmentType-filters-select"
                    isOptionEqualToValue={(option, value) => option === value}
                    label={t(I18N_NAMESPACE, documentsFiltersConfig[ATTACHMENT_TYPE_FILTER].label)}
                    limitTags={2}
                    name={ATTACHMENT_TYPE_FILTER}
                    options={attachmentTypes || []}
                    renderOption={(props, option) => {
                        const Icon = ModulesInfo[option].icon
                        return (
                            <li {...props}>
                                <Styled.ListItemIcon>
                                    <Icon color="objects/object-black" />
                                </Styled.ListItemIcon>
                                <ListItemText primary={t(I18N_NAMESPACE, ModulesInfo[option].labelKey)} />
                            </li>
                        )
                    }}
                    setFieldValue={setFieldValue}
                />
            }
            {
                enabledFilters.includes(AUTHOR_FILTER) && <Styled.AutoCompleteFilter
                    defaultValue={initialValues[AUTHOR_FILTER]}
                    disableClearable
                    getOptionLabel={option => option.uuid}
                    id="author-filters-select"
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    label={t(I18N_NAMESPACE, documentsFiltersConfig[AUTHOR_FILTER].label)}
                    limitTags={2}
                    name={AUTHOR_FILTER}
                    options={users || []}
                    filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase()
                        return options.filter(option => {
                            const fullName = `${option.first_name} ${option.last_name}`.toLowerCase()
                            return fullName.includes(inputValue) || option.email.toLowerCase().includes(inputValue)
                        })
                    }}
                    renderOption={(props, option) => {
                        const fullName = `${option?.first_name} ${option?.last_name}`
                        return <li {...props}>
                            <Styled.ListItemIcon>
                                <Styled.ChipAvatar size={24} src={option?.avatar} alt={fullName} />
                            </Styled.ListItemIcon>
                            <ListItemText primary={fullName} />
                        </li>
                    }}
                    renderValue={(selected, getTagProps) =>
                        selected.map((option, index) => {
                            const fullName = `${option.first_name} ${option.last_name}`
                            return <Chip
                                {...getTagProps({ index })}
                                key={option.uuid}
                                avatar={<Styled.ChipAvatar size={24} src={option.avatar} alt={fullName} />}
                                label={fullName}
                                color="cta-bg/cta-bg-primary"
                            />
                        })
                    }
                    setFieldValue={setFieldValue} />

            }
            {
                (enabledFilters.includes(DATE_FROM_FILTER)) ? <Styled.DatesContainer>
                    {
                        enabledFilters.includes(DATE_FROM_FILTER) && <FormikDatePicker
                            value={initialValues[DATE_FROM_FILTER]}
                            format="dd/MM/yyyy"
                            id="date-from-filters-select"
                            label={t(I18N_NAMESPACE, documentsFiltersConfig[DATE_FROM_FILTER].label)}
                            name={DATE_FROM_FILTER}
                            slotProps={{
                                field: {
                                    clearable: true,
                                },
                            }} />
                    }
                    {enabledFilters.includes(DATE_FROM_FILTER) && enabledFilters.includes(DATE_TO_FILTER)
                        && <Styled.DateSeparator> - </Styled.DateSeparator>}
                    {
                        enabledFilters.includes(DATE_TO_FILTER) && <FormikDatePicker
                            value={initialValues[DATE_TO_FILTER]}
                            id="date-from-filters-select"
                            label={t(I18N_NAMESPACE, documentsFiltersConfig[DATE_TO_FILTER].label)}
                            format="dd/MM/yyyy"
                            name={DATE_TO_FILTER}
                            slotProps={{
                                field: {
                                    clearable: true,
                                },
                            }} />
                    }
                </Styled.DatesContainer> : null
            }
            {
                enabledFilters.includes(TAGS_FILTER) && project?.tags && <Styled.AutoCompleteFilter
                    defaultValue={initialValues.tags}
                    disableClearable
                    getOptionLabel={option => option}
                    id="tags-filters-select"
                    isOptionEqualToValue={(option, value) => option === value}
                    label={t(I18N_NAMESPACE, documentsFiltersConfig[TAGS_FILTER].label)}
                    limitTags={2}
                    name={TAGS_FILTER}
                    options={project?.tags}
                    setFieldValue={setFieldValue} />
            }
        </Styled.InputsWrapper >
    }, [attachmentTypes, enabledFilters, fileTypes, initialValues, project, t, users])

    const getFormButtons = useCallback((resetForm) => {
        return <>
            {!isMobile && <Styled.Button
                id="reset-filters-button"
                color="error"
                onClick={() => {
                    resetForm({ values: defaultFormValues })
                    setFilters(defaultFormValues)
                    close()
                }}
                variant="text"
            >
                {t(I18N_NAMESPACE, "forms.documentFiltersForm.buttons.delete")}
            </Styled.Button>}
            <Styled.Wrapper isMobile={isMobile}>
                <Styled.Button
                    id="cancel-filters-button"
                    color="cta-bg/cta-bg-primary"
                    isMobile={isMobile}
                    onClick={() => {
                        resetForm({ values: { ...initialValues } })
                        close()
                    }}
                    variant="outlined"
                >
                    {t(I18N_NAMESPACE, "forms.documentFiltersForm.buttons.cancel")}
                </Styled.Button>
                <Styled.SubmitButton
                    id="submit-filters-button"
                    color="cta-bg/cta-bg-primary"
                    isMobile={isMobile}
                    type="submit"
                    variant="contained"
                >
                    {t(I18N_NAMESPACE, "forms.documentFiltersForm.buttons.confirm")}
                </Styled.SubmitButton>
            </Styled.Wrapper>
        </>
    }, [
        close,
        isMobile,
        initialValues,
        setFilters,
        t,
    ])

    const getForm = useCallback((resetForm, setFieldValue, values) => {
        if (isMobile) {
            return <Styled.Form>
                <ScrollableLayout
                    className={className}
                    content={getFormFields(setFieldValue, values)}
                    buttons={getFormButtons(resetForm)}
                />
            </Styled.Form>
        } else {
            return <Form>
                <Styled.FormWrapper>
                    {getFormFields(setFieldValue, values)}
                    <Styled.ButtonsWrapper>
                        {getFormButtons(resetForm)}
                    </Styled.ButtonsWrapper>
                </Styled.FormWrapper>
            </Form >
        }
    }, [getFormButtons, getFormFields, className, isMobile])

    return <Formik
        className={className}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={onSubmit}
    >
        {({ resetForm, setFieldValue, values }) => getForm(resetForm, setFieldValue, values)}
    </Formik >
}

DocumentFiltersForm.propTypes = {
    authors: PropTypes.arrayOf(PropTypes.string.isRequired),
    className: PropTypes.string,
    close: PropTypes.func.isRequired,
    enabledFilters: PropTypes.arrayOf(PropTypes.oneOf(FILTERS)).isRequired,
    fileTypes: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.node,
        name: PropTypes.string.isRequired,
        order: PropTypes.number.isRequired,
    })),
    onSubmit: PropTypes.func.isRequired,
    project: PropTypes.shape({
        created_at: PropTypes.string.isRequired,
        uuid: PropTypes.string.isRequired,
        tags: PropTypes.arrayOf(PropTypes.string.isRequired),
    }),
    setFilters: PropTypes.func.isRequired,
}
